import { MachineConfig, assign } from 'xstate';
import { StripeElements, Stripe } from '@stripe/stripe-js';
import {
  CreditCardContext,
  getSetupIntent,
  retrievePaymentMethods,
} from './creditCard';
import { saveEditCaseDraft } from '../../services/sessionStorage/editCaseDraft';

interface Context extends CreditCardContext {}

export type AddCreditCardEvent =
  | { type: 'CLOSE' }
  | {
      type: 'ADD_CARD';
      stripe: Stripe;
      elements: StripeElements;
    };

export type AddCreditCardState = {};

async function addNewPaymentMethod(context: CreditCardContext) {
  if (context.setupIntent) {
    // TODO cancel existing setup intent
  }

  const returnUrl = new URL(window.location.href);

  if (
    context.caseId &&
    'caseInfo' in context &&
    'paymentType' in context &&
    'caseType' in context
  ) {
    returnUrl.searchParams.set('loadDraft', 'true');

    saveEditCaseDraft(context.caseId, {
      caseId: context.caseId,
      caseInfo: (context as any).caseInfo,
      paymentType: (context as any).paymentType,
      selectedPaymentMethod: context.selectedPaymentMethod,
      selectedInvoiceAccountUid: (context as any).selectedInvoiceAccountUid,
      caseType: (context as any).caseType,
    });
  }

  const { setupIntent, error } = await context.stripe!.confirmSetup({
    elements: context.elements!,
    confirmParams: {
      return_url: returnUrl.toString(),
      payment_method_data: {
        billing_details: {
          name: context.name,
          email: context.email,
        },
      },
    },
    redirect: 'if_required',
  });

  if (error) {
    console.error(error);
    throw error;
  }
  if (
    !setupIntent ||
    (setupIntent.status !== 'processing' && setupIntent.status !== 'succeeded')
  ) {
    console.error('SetupIntent error', setupIntent);
    throw new Error('Failed to process payment method');
  }

  return setupIntent;
}

async function addAndRetrievePaymentMethod(context: Context) {
  const setupIntent = await addNewPaymentMethod(context);
  const paymentMethodResult = await retrievePaymentMethods(context);
  return {
    ...paymentMethodResult,
    // `setupIntent` from `paymentMethodResult` should be undefined here since that only exists
    // when being redirected back from Stripe after adding a new payment method
    // That redirect would have happened when we call `addNewPaymentMethod` so if we are at this point,
    // it means we are in the same session and not redirected back
    setupIntent,
    selectedPaymentMethod: setupIntent.payment_method,
  };
}

const addCreditCardStates: MachineConfig<Context, any, AddCreditCardEvent> = {
  initial: 'load',

  states: {
    load: {
      invoke: {
        src: getSetupIntent,
        onDone: {
          target: 'idle',
          actions: assign((context, ev) => {
            return {
              ...context,
              ...ev.data,
            };
          }),
        },
        onError: {
          // actions: escalate((context, ev) => ev.data),
          actions: assign({
            error: (context, ev) => ev.data,
          }),
        },
      },
    },

    idle: {
      on: {
        ADD_CARD: {
          actions: assign((context, ev) => {
            return {
              ...context,
              elements: ev.elements,
              stripe: ev.stripe,
            };
          }),
          target: 'addingCreditCard',
        },
      },
    },

    addingCreditCard: {
      invoke: {
        src: addAndRetrievePaymentMethod,
        onDone: {
          target: 'done',
          actions: assign((context, ev) => {
            return {
              ...context,
              ...ev.data,
            };
          }),
        },
        onError: {
          target: 'idle',
          // actions: escalate((context, ev) => ev.data),
          actions: assign({
            error: (context, ev) => ev.data,
          }),
        },
      },
    },

    done: {
      type: 'final',
    },
  },
};

export default addCreditCardStates;
